/* @flow */
import '../customer-animation.css';
import '../pizza-eating-view.css';

import { connect } from 'react-redux';
import { get } from 'lodash';
import * as React from 'react';

import { Box, Footer } from '@sonyged/proc-ui';
import { useSpring, animated } from 'react-spring';
import css from '@styled-system/css';
import styled from 'styled-components';

import { HEIGHT, WIDTH, customerLayer26Order, customerLayer5Order } from './constant';
import { Pizza } from '../../../pizza/fields/domain/pizza';
import { PizzaPoster } from './PizzaPoster';
import { ScalableContainer } from '../../../../../sushi/pages/Simulation/ScalableContainer';
import type { StoreState } from '../../../../redux/types';
import { Uriko } from './Uriko';
import AppContext from '../../../../AppContext';
import Comment from '../components/Comment';
import CustomerSimulation from './CustomerSimulation';
import pizza_exterior01 from '../../../../../assets/images/pages/pizza/fields/piza_fi-g5_exterior01@3x.png';
import pizza_exterior02 from '../../../../../assets/images/pages/pizza/fields/piza_fi-g5_exterior02@3x.png';

type OP = {|
  onNext: () => void,
  todayPizza: Pizza,
  customerCount: number,
  yesterdayCustomerCount: number,
  sale: number,
  score: number,
|};

type Props = {
  ...OP,
  avatar: ?string,
};

// 1: max, 2: medium, 3: min
function calculateGrade(score: number) {
  if (score < 0) return 3;
  if (score < 5) return 2;
  return 1;
}

const makeSpringConfig = (frequency: number, damping: number) => ({
  mass: 1,
  tension: Math.pow((2 * Math.PI) / frequency, 2),
  friction: (4 * Math.PI * damping) / frequency,
});

const hidden = { opacity: 0, transform: 'translate3d(0,5%,0)' };
const shown = { opacity: 1, transform: 'translate3d(0,0%,0)' };

export function SimulationContent(props: Props) {
  let { onNext, avatar, todayPizza, score, customerCount, yesterdayCustomerCount, sale } = props;
  let [scale, setScale] = React.useState(1);

  let { soundController = {} } = React.useContext(AppContext) || {};

  let grade = calculateGrade(score);
  let [showResult, setShowResult] = React.useState(false);

  let message = React.useMemo(() => {
    let rate = customerCount / yesterdayCustomerCount;
    if (rate > 1.3) return '昨日よりたくさんお客さんが来ているね。';
    if (rate < 0.8) return '昨日よりお客さんが少ないね。';
    return '昨日と同じぐらいのお客さんが来ているね。';
  }, [yesterdayCustomerCount, customerCount]);

  let [customers, p5, p6] = React.useMemo(() => {
    let customers, p5, p6;
    if (grade === 1) {
      customers = customerLayer26Order.slice(0, 16);
      p5 = customerLayer5Order.slice(0, 1);
      p6 = customerLayer26Order.slice(16, 17);
    } else if (grade === 2) {
      customers = customerLayer26Order.slice(0, 8);
      p5 = customerLayer5Order.slice(0, 5);
      p6 = customerLayer26Order.slice(8, 13);
    } else {
      customers = customerLayer26Order.slice(0, 2);
      p5 = customerLayer5Order.slice(0, 8);
      p6 = customerLayer26Order.slice(2, 10);
    }
    return [customers, p5, p6];
  }, [grade]);

  let [spring, setSpring] = useSpring(() => ({
    from: hidden,
    config: makeSpringConfig(0.3, 1),
  }));

  React.useEffect(() => {
    let id = setTimeout(() => setSpring(shown), 3000);
    return () => clearTimeout(id);
  }, []);

  React.useEffect(() => {
    let id = setTimeout(() => {
      setSpring(hidden);
      setShowResult(true);
      soundController.sePlay('stamp');
    }, 8000);
    return () => clearTimeout(id);
  }, []);

  return (
    <Container className="pizza-eating-view">
      <AnimationContainer
        className="proc-pizza-field-content simulation pizza-viewport"
        dHeight={HEIGHT}
        dWidth={WIDTH}
        dMinHeight={0}
        dMinWidth={0}
        onScaleChange={setScale}
      >
        <Background className="animation-area">
          <Bg src={pizza_exterior01} />
          <CustomerSimulation bottom={0.3} customers={customers} scale={scale} onAnimationEnd={() => {}} />
          <Bg src={pizza_exterior02} />
          <Uriko scale={scale} avatar={avatar} />
          <PizzaPoster scale={scale} pizza={todayPizza} />
          <CustomerSimulation flip loop bottom={0.2} customers={p5} scale={scale} />
          <CustomerSimulation loop bottom={0} customers={p6} scale={scale} />
        </Background>

        {showResult &&
          (() => {
            const containerStyle = {
              maxWidth: scale * 368,
              height: scale * 160,
            };

            const stampStyle = {
              bottom: 10 * scale,
              right: 10 * scale,
              width: 82 * scale,
              height: 82 * scale,
            };

            const smallFontSize = {
              fontSize: 15 * scale,
            };

            const amountFontSize = {
              fontSize: 50 * scale,
            };

            const yenFontSize = {
              fontSize: 32 * scale,
            };

            return (
              <div
                style={containerStyle}
                className={'pizza-price-container proc-border-radius secondary20-border-color center-aligned'}
              >
                <div className={'background absolute white-bg-color'} />
                <div className={'content absolute'}>
                  <div style={smallFontSize} className={'title secondary100-color'}>
                    もらった お金{' '}
                  </div>
                  <div style={amountFontSize} className={'gray100-color'}>
                    {sale}
                    <span style={yenFontSize}>円</span>
                  </div>
                  <div style={stampStyle} className={`pizza-stamp pizza-stamp--${grade}`} />
                </div>
              </div>
            );
          })()}
      </AnimationContainer>
      <Box position="relative" width="100%" maxWidth={1024} alignSelf="center">
        <AnimatedComment style={spring}>{message}</AnimatedComment>
      </Box>
      <Footer>
        <Footer.MainButton disabled={!showResult} onClick={onNext}>
          つぎへ
        </Footer.MainButton>
      </Footer>
    </Container>
  );
}

const Container = styled(Box)(
  css({
    display: 'flex',
    flexDirection: 'column',
    size: '100%',
  })
);

const AnimationContainer = styled(ScalableContainer)(css({ flex: 1, minHeight: 0 }));

const Background = styled(Box)(
  css({
    position: 'relative',
    height: '100%',
    backgroundColor: 'white',
  })
);

const Bg = styled(Box)(({ src }) =>
  css({
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    background: `url(${src}) no-repeat`,
    backgroundSize: '100%',
  })
);

const AnimatedComment = animated(Comment);

const mapStateToProps = (state: StoreState) => ({
  avatar: get(state, 'profile.avatar'),
});

export default connect<Props, OP, _, _, _, _>(mapStateToProps)(SimulationContent);
